<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <button type="button" class="btn-back" @click="$router.push('/client/inventory/purchase-orders')">
          <i class="ri-arrow-left-line"></i>
        </button>
        <h1 class="page-title">新增進貨單</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="$router.push('/client/inventory/purchase-orders')">取消</button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="submitForm">
          <span v-if="saving"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else><i class="ri-check-line"></i> 建立進貨單</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-container">

    <div class="form-container">
      <!-- 基本資訊 -->
      <div class="form-section">
        <h3 class="section-title">基本資訊</h3>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label required">入庫倉庫</label>
            <select v-model="form.warehouseId" class="form-control">
              <option value="">請選擇倉庫</option>
              <option v-for="w in warehouses" :key="w.id" :value="w.id">
                {{ w.warehouseName }}（{{ w.warehouseType === 'main' ? '總倉' : '分倉' }}）
              </option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">預計到貨日</label>
            <input v-model="form.expectedDate" type="date" class="form-control" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">供應商名稱</label>
            <input v-model="form.supplierName" class="form-control" placeholder="選填" />
          </div>
          <div class="form-group">
            <label class="form-label">供應商聯絡</label>
            <input v-model="form.supplierContact" class="form-control" placeholder="電話或 Email（選填）" />
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">備註</label>
          <textarea v-model="form.notes" class="form-control" rows="2" placeholder="選填"></textarea>
        </div>
      </div>

      <!-- 商品明細 -->
      <div class="form-section">
        <div class="section-header">
          <h3 class="section-title">商品明細</h3>
          <div class="section-actions">
            <!-- 條碼輸入 -->
            <div class="barcode-row">
              <input
                v-model="barcodeQuery"
                type="text"
                class="form-control barcode-input"
                placeholder="掃描條碼按 Enter"
                :disabled="barcodeLoading"
                @keydown.enter.prevent="handleBarcodeAdd"
              />
              <button class="btn btn-secondary btn-sm" :disabled="!barcodeQuery.trim() || barcodeLoading" @click="handleBarcodeAdd">
                <i class="ri-barcode-line"></i> {{ barcodeLoading ? '查詢中...' : '條碼' }}
              </button>
            </div>
            <button class="btn btn-secondary btn-sm" @click="showPicker = true">
              <i class="ri-list-check"></i> 挑選商品
            </button>
          </div>
        </div>
        <span v-if="barcodeError" class="barcode-error">{{ barcodeError }}</span>

        <!-- 明細表格 -->
        <div class="items-wrapper">
          <div v-if="!form.items.length" class="items-empty">
            <i class="ri-inbox-line"></i>
            <span>請點選「挑選商品」或掃描條碼新增明細</span>
          </div>

          <div v-for="(item, idx) in form.items" :key="idx" class="item-card">
            <div class="item-info">
              <span class="item-name">{{ item.productName }}</span>
              <span class="item-sku">{{ item.variantSku }}</span>
              <span v-if="item.variantAttrs" class="item-attrs">{{ item.variantAttrs }}</span>
            </div>
            <div class="item-fields">
              <div class="field-group">
                <span class="field-label">採購數量</span>
                <input v-model.number="item.orderedQuantity" type="number" min="1" class="form-control field-input" />
              </div>
              <div class="field-group">
                <span class="field-label">採購單價</span>
                <input v-model.number="item.unitCost" type="number" min="0" step="0.01" class="form-control field-input" placeholder="0" />
              </div>
              <div class="field-group subtotal-group">
                <span class="field-label">小計</span>
                <span class="field-value">{{ formatCurrency(item.orderedQuantity * (item.unitCost || 0)) }}</span>
              </div>
              <div class="field-group notes-group">
                <span class="field-label">備註</span>
                <input v-model="item.notes" class="form-control field-input-wide" placeholder="選填" />
              </div>
              <button class="btn-delete" @click="removeItem(idx)" title="移除">
                <i class="ri-delete-bin-line"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- 合計 -->
        <div v-if="form.items.length" class="items-total">
          <span>採購合計：</span>
          <strong>{{ formatCurrency(totalAmount) }}</strong>
        </div>
      </div>

    <!-- 共用商品挑選 Modal（variant 必選模式）-->
    <ProductPickerModal
      :open="showPicker"
      title="挑選進貨商品"
      :variant-required="true"
      @close="showPicker = false"
      @select="onProductSelected"
    />
    </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { purchaseOrderService, warehouseService } from '@/api/modules/warehouses.js';
import { productService } from '@/api/modules/products.js';
import ProductPickerModal from '@/components/ProductPickerModal.vue';

const router = useRouter();
const saving = ref(false);
const warehouses = ref([]);
const showPicker = ref(false);
const barcodeQuery = ref('');
const barcodeLoading = ref(false);
const barcodeError = ref('');

const form = ref({
  warehouseId: '',
  supplierName: '',
  supplierContact: '',
  expectedDate: '',
  notes: '',
  items: []
});

const totalAmount = computed(() =>
  form.value.items.reduce((sum, i) => sum + i.orderedQuantity * (i.unitCost || 0), 0)
);

const formatCurrency = (v) => `$${Number(v || 0).toLocaleString()}`;

async function fetchWarehouses() {
  try {
    const res = await warehouseService.getWarehouses({ status: 'active' });
    warehouses.value = res.data?.items || [];
  } catch (e) { console.error(e); }
}

/** 商品挑選 Modal 選完後的 callback */
function onProductSelected({ product, variant }) {
  if (!variant) return; // variantRequired = true，通常不會發生

  const variantAttrs = variant.attributes && Object.keys(variant.attributes).length
    ? Object.values(variant.attributes).filter(Boolean).join(' / ')
    : '';
  const displayName = variantAttrs
    ? `${product.name} - ${variantAttrs}`
    : product.name;

  const exists = form.value.items.find(i => i.variantId === variant.id);
  if (exists) {
    exists.orderedQuantity += 1;
    return;
  }
  form.value.items.push({
    variantId: variant.id,
    productName: displayName,
    variantSku: variant.sku || product.sku || '',
    variantAttrs,
    orderedQuantity: 1,
    unitCost: Number(variant.unitPrice ?? product.unitPrice) || 0,
    notes: ''
  });
}

/** 條碼加入 */
async function handleBarcodeAdd() {
  const barcode = barcodeQuery.value.trim();
  if (!barcode) return;
  barcodeError.value = '';
  barcodeLoading.value = true;
  try {
    const res = await productService.getByBarcode(barcode);
    if (!res?.success || !res.data) {
      barcodeError.value = '找不到此條碼對應的商品';
      return;
    }
    const d = res.data;
    const variantId = d.type === 'variant' ? d.id : null;
    if (!variantId) {
      barcodeError.value = '請從商品挑選器選取規格，或使用含規格的條碼';
      return;
    }
    const exists = form.value.items.find(i => i.variantId === variantId);
    if (exists) {
      exists.orderedQuantity += 1;
    } else {
      form.value.items.push({
        variantId,
        productName: d.productName || d.name || '商品',
        variantSku: d.sku || d.productSku || '',
        variantAttrs: '',
        orderedQuantity: 1,
        unitCost: Number(d.unitPrice) || 0,
        notes: ''
      });
    }
    barcodeQuery.value = '';
  } catch (e) {
    barcodeError.value = e.message || '查詢失敗';
  } finally {
    barcodeLoading.value = false;
  }
}

function removeItem(idx) {
  form.value.items.splice(idx, 1);
}

async function submitForm() {
  if (!form.value.warehouseId) { alert('請選擇入庫倉庫'); return; }
  if (!form.value.items.length) { alert('請新增至少一筆商品明細'); return; }
  for (const item of form.value.items) {
    if (!item.orderedQuantity || item.orderedQuantity < 1) { alert(`「${item.productName}」採購數量必須大於 0`); return; }
  }

  saving.value = true;
  try {
    const payload = {
      warehouseId: form.value.warehouseId,
      supplierName: form.value.supplierName || undefined,
      supplierContact: form.value.supplierContact || undefined,
      expectedDate: form.value.expectedDate || undefined,
      notes: form.value.notes || undefined,
      items: form.value.items.map(i => ({
        variantId: i.variantId,
        orderedQuantity: i.orderedQuantity,
        unitCost: i.unitCost || 0,
        notes: i.notes || undefined
      }))
    };
    const res = await purchaseOrderService.createPurchaseOrder(payload);
    const id = res.data?.id;
    router.push(id ? `/client/inventory/purchase-orders/${id}` : '/client/inventory/purchase-orders');
  } catch (e) {
    alert(e.response?.data?.message || e.message || '建立失敗');
  } finally {
    saving.value = false;
  }
}

onMounted(fetchWarehouses);
</script>

<style scoped>
.create-container { max-width: 900px; margin: 0 auto; padding: 20px; }

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.btn-back {
  background: none;
  border: 1px solid #ddd;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #555;
}
.btn-back:hover { background: #f5f5f5; }

.page-title { font-size: 22px; font-weight: 600; margin: 0; }

.form-container {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
}

.form-section { margin-bottom: 32px; }
.form-section:last-child { margin-bottom: 0; }

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.section-header .section-title { margin-bottom: 0; border: none; padding: 0; }

.section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group { display: flex; flex-direction: column; }
.form-label { font-size: 13px; font-weight: 500; color: #555; margin-bottom: 5px; }
.form-label.required::after { content: ' *'; color: #e74c3c; }
.form-control {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}
.form-control:focus { outline: none; border-color: #1a73e8; box-shadow: 0 0 0 2px rgba(26,115,232,.15); }

/* 條碼列 */
.barcode-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.barcode-input { width: 180px; }
.barcode-error { display: block; color: #e74c3c; font-size: 13px; margin-bottom: 8px; }

/* 明細 */
.items-wrapper { border: 1px solid #e8e8e8; border-radius: 8px; overflow: hidden; margin-bottom: 12px; }

.items-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: #aaa;
  font-size: 14px;
}
.items-empty i { font-size: 24px; }

.item-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
}
.item-card:last-child { border-bottom: none; }
.item-card:nth-child(even) { background: #fafafa; }

.item-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 160px;
  flex: 1;
}
.item-name { font-size: 14px; font-weight: 600; color: #222; }
.item-sku { font-size: 12px; color: #888; font-family: monospace; }
.item-attrs { font-size: 12px; color: #666; }

.item-fields {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.field-group { display: flex; flex-direction: column; gap: 3px; }
.field-label { font-size: 11px; color: #888; font-weight: 500; white-space: nowrap; }
.field-input { width: 88px; padding: 5px 8px; height: 34px; }
.field-input-wide { width: 140px; padding: 5px 8px; height: 34px; }

.subtotal-group { min-width: 72px; }
.field-value { font-weight: 600; font-size: 14px; color: #222; line-height: 34px; }

.notes-group .form-control { font-size: 13px; }

.btn-delete {
  background: none;
  border: none;
  color: #bbb;
  cursor: pointer;
  font-size: 18px;
  padding: 6px;
  border-radius: 4px;
  align-self: flex-end;
  height: 34px;
  display: flex;
  align-items: center;
}
.btn-delete:hover { color: #e74c3c; background: #fff0f0; }

.items-total {
  text-align: right;
  font-size: 15px;
  color: #555;
  padding: 8px 4px;
}
.items-total strong { color: #1a73e8; font-size: 17px; margin-left: 6px; }

/* 操作按鈕 */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
}

.btn {
  padding: 9px 20px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 14px;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}
.btn-primary { background: #1a73e8; color: #fff; }
.btn-primary:hover { background: #1558b0; }
.btn-secondary { background: #f0f0f0; color: #333; }
.btn-secondary:hover { background: #e0e0e0; }
.btn-sm { padding: 5px 12px; font-size: 13px; }
.btn:disabled { opacity: .6; cursor: not-allowed; }

.spin {
  display: inline-block;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 700px) {
  .form-row { grid-template-columns: 1fr; }
  .section-header { flex-direction: column; align-items: flex-start; gap: 10px; }
  .item-card { flex-direction: column; }
  .barcode-input { width: 120px; }
}
</style>
